import React, { FC, useEffect, useState } from 'react';
import StoreModel from '../../models/Index';
import { observer, inject } from 'mobx-react';
import BasicDialog from 'components/modals/BasicModal';
import Autocomplete from '@mui/lab/Autocomplete';
import { LocationModel } from 'models/shared/LocationModel';
import { Services } from '../../helpers/constants/Services';
import { PrevButton, NextButton } from 'components/wizard';
import {
    Box,
    Switch,
    MenuItem,
    Typography,
    FormControl,
    FormControlLabel,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { history } from 'helpers/History';
import { RoutePaths } from 'helpers/constants/RoutePaths';
import { RedditSelect, RedditTextField } from 'components/inputs';

const useStyles = makeStyles((theme) => {
    return {
        loginText: { marginTop: 100 },
        selectOption: {
            fontSize: '2rem',
        },
        selectOptionGroupLabel: {
            fontSize: '1.5rem',
        },
        selectWrapper: {
            margin: '30px auto',
            display: 'flex',
            maxWidth: '450px',
        },
        switchLabel: {
            fontSize: '1.75rem !important',
        },
    };
});

interface RegistrationProps {}
interface RegistrationPropsInjected extends RegistrationProps {
    Store: StoreModel;
}

const Registration: FC<RegistrationProps> = (props) => {
    const { Store } = props as RegistrationPropsInjected;
    const registration = Store.admin.registration;
    const {
        registers,
        locations,
        brands,
        registersLoaded,
        selectedLocation,
        selectedRegister,
        selectedBrand,
        handleSwitchChange,
        handleRegisterChange,
        handleLocationChange,
        handleBrandChange,
        selectedRegisterLocationName,
    } = registration;
    const classes = useStyles();

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    useEffect(() => {
        Store.admin
            .login()
            .then(registration.getLocations)
            .then(registration.getExistingRegister);

        return () => {
            Store.admin.registration.reset();
        };
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Box>
                <Typography color='primary' variant='h2' align='center'>
                    <Box className={classes.loginText} my={4} py={4}>
                        Kiosk setup
                    </Box>
                </Typography>
                <Typography variant='h4' align='center'>
                    <Box mt={4} mb={1} py={4} fontWeight={500}>
                        Please select location and register
                    </Box>
                </Typography>
                <FormControl
                    className={classes.selectWrapper}
                    variant='outlined'
                >
                    <Autocomplete
                        id='location'
                        fullWidth
                        options={locations!.slice()}
                        value={selectedLocation || null}
                        noOptionsText='No stores available'
                        groupBy={(location) => location.parentName!}
                        getOptionLabel={(option) => option.displayName}
                        onChange={(event: any, value: LocationModel | null) =>
                            handleLocationChange(value)
                        }
                        classes={{
                            option: classes.selectOption,
                            groupLabel: classes.selectOptionGroupLabel,
                        }}
                        renderInput={(renderProps) => (
                            <RedditTextField
                                {...renderProps}
                                margin='normal'
                                label='Location'
                                variant='filled'
                                InputProps={{
                                    ...renderProps.InputProps,
                                }}
                                InputLabelProps={{
                                    ...renderProps.InputLabelProps,
                                }}
                            />
                        )}
                    />
                </FormControl>
                <FormControl className={classes.selectWrapper} variant='filled'>
                    <RedditSelect
                        inputId='register'
                        inputLabelText='Register'
                        disabled={!registers.length}
                        value={selectedRegister.registerId ?? ''}
                        onChange={(event) =>
                            handleRegisterChange(Number(event.target.value))
                        }
                    >
                        {registers.map((r) => (
                            <MenuItem
                                className={classes.selectOption}
                                key={r.registerId!}
                                value={r.registerId!}
                            >
                                {r.registerName}
                            </MenuItem>
                        ))}
                    </RedditSelect>
                </FormControl>
                <FormControl className={classes.selectWrapper} variant='filled'>
                    <RedditSelect
                        inputId='brand'
                        inputLabelText='Brand'
                        labelId='brand'
                        value={selectedBrand}
                        onChange={(event) =>
                            handleBrandChange(event.target.value as string)
                        }
                    >
                        {brands.map((r) => (
                            <MenuItem
                                className={classes.selectOption}
                                key={r}
                                value={r}
                            >
                                {r}
                            </MenuItem>
                        ))}
                    </RedditSelect>
                </FormControl>
                {registersLoaded && !registers.length && (
                    <Box mt={4} mb={4} color='red'>
                        <Typography variant='h4' display='block' align='center'>
                            There are currently no registers associated with
                            this Kiosk.
                        </Typography>
                        <Typography variant='h4' display='block' align='center'>
                            Please contact your System Administrator to assign a
                            register.
                        </Typography>
                    </Box>
                )}
                <Typography variant='h4' align='center'>
                    <Box
                        mt={4}
                        mb={1}
                        py={4}
                        color='gray'
                        fontSize='1.6rem'
                        fontStyle='italic'
                        fontWeight={400}
                    >
                        Switch off to make Kiosk "Out of order"
                    </Box>
                </Typography>
                <FormControl
                    className={classes.selectWrapper}
                    variant='outlined'
                >
                    <FormControlLabel
                        label={selectedRegister.status}
                        control={
                            <Switch
                                value='active'
                                color='primary'
                                checked={selectedRegister?.isActive}
                                onChange={(event, checked) =>
                                    handleSwitchChange(checked)
                                }
                            />
                        }
                        classes={{ label: classes.switchLabel }}
                    />
                </FormControl>
            </Box>
            <Box display='flex' m={4} py={4} justifyContent='space-between'>
                <PrevButton
                    buttonProps={{
                        onClick: () =>
                            history.push({
                                pathname: RoutePaths.AdminHome,
                                search: window.location.search,
                            }),
                    }}
                />
                <NextButton
                    title={'Next'}
                    buttonProps={{
                        disabled: !selectedRegister.registerId,
                        onClick: () => setIsDialogOpen(true),
                    }}
                />
            </Box>
            <BasicDialog
                title='Kiosk setup confirmation'
                modalProps={{ open: isDialogOpen }}
                buttonCancelProps={{
                    name: 'Cancel',
                    onClick: () => setIsDialogOpen(false),
                }}
                buttonConfirmProps={{
                    name: 'Confirm',
                    onClick: () => {
                        return registration
                            .processAlticeKioskRegister()
                            .finally(() => setIsDialogOpen(false));
                    },
                }}
            >
                <Box>
                    <Box color='black'>
                        This kiosk will be linked to &nbsp;
                        <strong>{selectedRegisterLocationName}</strong> location
                        and <strong>{selectedRegister.registerName}</strong>{' '}
                        register is <strong>{selectedRegister.status}</strong>
                    </Box>
                    <Box py={2} color='black'>
                        Press Confirm button to proceed
                    </Box>
                </Box>
            </BasicDialog>
        </>
    );
};

export default inject(Services.STORE)(observer(Registration));
